/**
 * /el/guides/fotografies-kalesmenon-gamou
 *
 * The Greek counterpart of `how-to-collect-wedding-photos-from-guests.en.js`,
 * and the article that opens the Greek photo cluster.
 *
 * ── Its relationship to the QR guide, which is the whole design ──────────
 * Greek search intent around guest photos and around QR codes overlaps almost
 * completely: «πώς να μαζέψω φωτογραφίες από τους καλεσμένους» and «QR code
 * γάμου» return much the same results, because the QR framing has already won
 * the category here. Two Greek articles chasing that one query would make
 * Agapaé its own only competitor on its most distinctive feature.
 *
 * So the pair is split by PURPOSE rather than by keyword, and the split is
 * strict:
 *
 *   - THIS article owns the decision: what the options are, what each costs a
 *     guest, what happens to the media, how you get people to take part, and
 *     the practical plan for the day. A QR code appears once, as one way of
 *     reaching a destination.
 *   - `wedding-qr-code-for-photos.el.js` owns the mechanism: what happens when
 *     someone scans, where to put the code, what to test beforehand.
 *
 * When this article was written, the QR guide carried a merged four-option
 * comparison, because there was no Greek collection guide to hold it. That
 * section was removed in the same change that added this file, and replaced
 * with a link here. Nothing in this article may drift into QR generation,
 * printing, placement or troubleshooting, and nothing in the QR guide may take
 * the comparison back. If both articles end up answering "which method should
 * I use", one of them is redundant.
 *
 * ── The hedges, which are load-bearing ───────────────────────────────────
 * No method is described as universally best and none is dismissed. The
 * comparison exists to expose a trade-off — guest effort against organisation
 * against what happens to the files — not to walk the reader to a conclusion.
 * Three must survive editing: messaging apps MAY compress or process media
 * depending on the platform and on how it was sent; shared-album services
 * differ in what they ask of a guest; and asking after the wedding is
 * described as extra friction, never as something that fails. A couple whose
 * family Viber group worked perfectly well is not doing it wrong.
 *
 * Every quantified participation claim is banned: no multiples, no share of
 * guests, no number of photographs a wedding produces. None of it is measured.
 *
 * ── The claims this guide is allowed to make ─────────────────────────────
 * Six, all in the closing section, identical to the English module's:
 *
 *   - the guest gallery is part of every Agapaé plan, with different limits
 *     on each  (the note) — NOT "photo and video": Blossom is photos only
 *   - guests upload photos and videos (videos are plan-dependent)
 *   - they reach it through the event's guest upload flow
 *   - a QR code or a link is how they reach it
 *   - the gallery is private to the event rather than a public feed
 *   - the plan sets the limits: how many guests, how much the gallery holds,
 *     how long the window stays open, and whether guests can add videos
 *
 * Never: «χωρίς λογαριασμό» or «χωρίς εφαρμογή» as a guarantee, original
 * quality on every plan, «απεριόριστα», «για πάντα», «εγγυημένα», automatic
 * backup, automatic sharing, automatic organisation, AI selection, a live
 * slideshow, upload notifications, or any link between a guest's identity and
 * the files they added.
 *
 * No copy in this file contains "&", "<" or ">".
 */

const howToCollectWeddingPhotosFromGuestsEl = {
  slug: "fotografies-kalesmenon-gamou",

  title: "Πώς θα μαζέψετε τις φωτογραφίες των καλεσμένων από τον γάμο σας",
  metaTitle: "Πώς θα μαζέψετε τις φωτογραφίες των καλεσμένων | Agapaé",
  metaDescription:
    "Όλοι οι τρόποι να μαζέψετε τις φωτογραφίες των καλεσμένων: κοινόχρηστο άλμπουμ, group στο κινητό, αίτημα μετά τον γάμο ή σελίδα ανεβάσματος — και τι κοστίζει ο καθένας.",

  eyebrow: "Φωτογραφίες γάμου",

  hero: {
    src: "/assets/images/invitation/invitation-2.jpg",
    alt: "Ασπρόμαυρη φωτογραφία τελετής γάμου σε εξωτερικό χώρο, τραβηγμένη μέσα από τους καλεσμένους, με σειρές καθισμένων ανθρώπων εκατέρωθεν και το ζευγάρι να στέκεται ανάμεσα σε δύο ψηλές ανθοστήλες",
    width: 6558,
    height: 3659,
  },

  intro: [
    `Ο φωτογράφος σας καταγράφει την ιστορία που σχεδιάσατε: το μυστήριο, τα πορτρέτα, τον πρώτο χορό. Οι καλεσμένοι καταγράφουν όλα τα υπόλοιπα, από εκεί όπου τυχαίνει να στέκονται.`,
    `Οι περισσότερες από αυτές τις φωτογραφίες δεν φτάνουν ποτέ σε εσάς. Μένουν στα κινητά, καμιά δεκαριά εμφανίζονται σε ένα group, και οι υπόλοιπες ξεχνιούνται.`,
    `Η δυσκολία σχεδόν ποτέ δεν είναι να βγάλουν φωτογραφίες οι καλεσμένοι. Είναι η επιστροφή: να φτάσουν πίσω σε εσάς σε μορφή που μπορείτε να κρατήσετε.`,
    `Αυτός ο οδηγός είναι για αυτό το κενό, και για το πώς κλείνει με μία διαδρομή από τα κινητά των καλεσμένων σε ένα σημείο που σας ανήκει.`,
  ],

  sections: [
    {
      heading: "Γιατί αξίζουν οι φωτογραφίες των καλεσμένων",
      content: [
        `Δεν είναι μια φθηνότερη εκδοχή της επαγγελματικής φωτογράφισης. Είναι άλλη οπτική γωνία, από εκεί όπου δεν ήταν ο φωτογράφος σας.`,
        `Συνήθως κρατούν:`,
        {
          type: "list",
          items: [
            `τι γινόταν στα άλλα τραπέζια`,
            `τις αντιδράσεις στις ευχές, αντί για αυτόν που μιλούσε`,
            `την πίστα αργά το βράδυ, χωρίς πόζα`,
            `την προετοιμασία και τις ήσυχες ώρες πριν από το μυστήριο`,
            `τα άτυπα πορτρέτα ανάμεσα στα προγραμματισμένα`,
            `στιγμές πριν φτάσει ή αφού έφυγε ο φωτογράφος`,
          ],
        },
        `Τίποτα από αυτά δεν ανταγωνίζεται τη δουλειά του φωτογράφου σας. Στέκεται δίπλα της, και είναι το κομμάτι που οι καλεσμένοι θέλουν περισσότερο να σας στείλουν.`,
      ],
    },
    {
      heading: "Τι κοινό έχουν οι τρόποι που δουλεύουν",
      content: [
        `Κανένας τρόπος δεν ταιριάζει σε κάθε γάμο, αλλά όσοι δουλεύουν μοιράζονται τρία πράγματα.`,
        {
          type: "list",
          items: [
            `Ένα προφανές σημείο, ώστε να μη χρειάζεται να ρωτήσει κανείς πού πάνε οι φωτογραφίες.`,
            `Ελάχιστο κόπο για τον καλεσμένο, γιατί το αίτημά σας ανταγωνίζεται μια γιορτή.`,
            `Μια συλλογή στην οποία μπορείτε πραγματικά να μπείτε και μετά.`,
          ],
        },
        `Κάθε τρόπος παρακάτω είναι μια απόπειρα να κλείσει η ίδια σύντομη αλυσίδα: ο καλεσμένος βγάζει μια φωτογραφία, τη μοιράζεται, εσείς τη μαζεύετε, η συλλογή μένει οργανωμένη και γίνεται μέρος του αρχείου του γάμου σας.`,
        `Οι περισσότεροι σπάνε στον δεύτερο κρίκο. Ο καλεσμένος που πρέπει να θυμηθεί μια διαδικασία μέρες αργότερα έχει ήδη προχωρήσει. Ο καλεσμένος που του ζητάτε να μοιραστεί τη φωτογραφία που κοιτάζει εκείνη τη στιγμή δεν έχει σχεδόν τίποτα να κάνει.`,
      ],
    },
    {
      heading: "Τρόπος 1: Ένα κοινόχρηστο άλμπουμ",
      content: [
        `Το μοντέλο είναι γνωστό: φτιάχνετε ένα κοινό άλμπουμ, δίνετε πρόσβαση στους καλεσμένους και ό,τι προσθέτουν μαζεύεται σε ένα σημείο. Το Google Photos και τα κοινόχρηστα άλμπουμ του iPhone είναι τα πιο συνηθισμένα παραδείγματα.`,
        { type: "subheading", text: "Τι πάει καλά" },
        {
          type: "list",
          items: [
            `πολλοί καλεσμένοι έχουν ξαναχρησιμοποιήσει κάτι παρόμοιο`,
            `οι φωτογραφίες μαζεύονται σε ένα άλμπουμ και όχι σε ξεχωριστά μηνύματα`,
            `συχνά βλέπουν και οι ίδιοι τι έχουν προσθέσει οι άλλοι`,
            `δεν υπάρχει επιπλέον κόστος, αν ζείτε ήδη σε αυτό το οικοσύστημα`,
          ],
        },
        { type: "subheading", text: "Πού δυσκολεύει" },
        {
          type: "list",
          items: [
            `ανάλογα με την υπηρεσία, ο καλεσμένος μπορεί να χρειαστεί συγκεκριμένο λογαριασμό ή πρόσκληση`,
            `το τι μπορεί να κάνει διαφέρει από πλατφόρμα σε πλατφόρμα και από κινητό σε κινητό`,
            `οι ρυθμίσεις κοινοποίησης κρίνουν ποιος βλέπει και ποιος προσθέτει, οπότε δείτε τις νωρίς`,
            `αξίζει να επιβεβαιώσετε πώς και σε τι ποιότητα κρατιούνται τα αρχεία`,
          ],
        },
        `Το κοινόχρηστο άλμπουμ αποκλείει σιωπηλά ακριβώς τους καλεσμένους που δυσκολεύονται περισσότερο — όσους δεν έχουν τον λογαριασμό που ζητάει, ή δεν θυμούνται τον κωδικό του.`,
      ],
    },
    {
      heading: "Τρόπος 2: Ένα group στο κινητό",
      content: [
        `Οι περισσότεροι γάμοι καταλήγουν εδώ χωρίς να το αποφασίσουν: οι καλεσμένοι στέλνουν φωτογραφίες στο Viber, στο WhatsApp ή σε όποιο group υπάρχει ήδη.`,
        { type: "subheading", text: "Γιατί δουλεύει" },
        {
          type: "list",
          items: [
            `δεν υπάρχει τίποτα να μάθει ή να εξηγηθεί`,
            `όλοι ξέρουν ήδη να στέλνουν μια φωτογραφία έτσι`,
            `δεν χρειάζεται να συστήσετε κανέναν νέο προορισμό μέσα στη γιορτή`,
          ],
        },
        { type: "subheading", text: "Πού αρχίζει να χαλάει" },
        {
          type: "list",
          items: [
            `οι φωτογραφίες μπερδεύονται με κουβέντες, αστεία και πρακτικά`,
            `συνήθως υπάρχουν πολλά group, οπότε η συλλογή είναι ήδη μοιρασμένη`,
            `το να βρείτε μία φωτογραφία μήνες μετά σημαίνει να κυλήσετε μια ολόκληρη συζήτηση`,
            `οι εφαρμογές μηνυμάτων ενδέχεται να συμπιέσουν ή να επεξεργαστούν το υλικό, ανάλογα με την πλατφόρμα και με το πώς στάλθηκε`,
            `τα βίντεο είναι το πιο άβολο κομμάτι, και αυτό που πιο συχνά δεν περνά`,
          ],
        },
        `Ένα group είναι μια συζήτηση που τυχαίνει να περιέχει εικόνες. Αυτό που σας κοστίζει φωτογραφίες είναι η αταξία, περισσότερο από την ποιότητα.`,
      ],
    },
    {
      heading: "Τρόπος 3: Να τις ζητήσετε μετά τον γάμο",
      content: [
        `Το «στείλτε μας τις φωτογραφίες σας όταν προλάβετε» δουλεύει, ιδίως με έναν μικρό κύκλο κοντινών ανθρώπων.`,
        `Μέχρι τότε όμως οι φωτογραφίες έχουν θαφτεί κάτω από ό,τι τραβήχτηκε στο μεταξύ:`,
        {
          type: "list",
          items: [
            `ο καθένας έχει προχωρήσει στην επόμενη εβδομάδα του`,
            `οι καλές προθέσεις ξεθωριάζουν, και κανείς δεν θέλει να πιέσει`,
            `οι φωτογραφίες κάθονται σε ένα κινητό που κανείς δεν ξαναγυρίζει τόσο πίσω`,
            `όσα φτάνουν, φτάνουν με email, μηνύματα και συνδέσμους, χωρίς σειρά`,
          ],
        },
        `Το να ζητήσετε μετά δεν αποτυγχάνει. Απλώς προσθέτει μια ενέργεια αφού έχουν τελειώσει όλα, και κάθε επιπλέον ενέργεια σας κοστίζει ένα κομμάτι της συλλογής.`,
      ],
    },
    {
      heading: "Τρόπος 4: Μια σελίδα ανεβάσματος για τον γάμο σας",
      content: [
        `Ο τέταρτος τρόπος είναι ένας προορισμός φτιαγμένος ακριβώς γι' αυτό: μια σελίδα όπου οι καλεσμένοι ανεβάζουν τις φωτογραφίες και τα βίντεό τους, σε μία συλλογή που σας ανήκει.`,
        `Από τη μεριά του καλεσμένου, η όλη εμπειρία είναι τρεις κινήσεις:`,
        {
          type: "steps",
          items: [
            `Ανοίγει τη σελίδα.`,
            `Διαλέγει τι θέλει να μοιραστεί.`,
            `Το ανεβάζει.`,
          ],
        },
        {
          type: "p",
          text: [
            `Ένας σύνδεσμος αρκεί. Μέσα σε μια αίθουσα, ένα QR code είναι πιο βολικό, γιατί γλιτώνει την πληκτρολόγηση — είναι τρόπος να φτάσει κανείς στη σελίδα, όχι διαφορετική μέθοδος. Το πώς ακριβώς δουλεύει είναι το θέμα του οδηγού `,
            {
              text: "QR code γάμου για φωτογραφίες: πώς λειτουργεί",
              href: "/guides/wedding-qr-code-for-photos",
            },
            `.`,
          ],
        },
        `Αυτό που το ξεχωρίζει από τους άλλους τρεις είναι ότι το ανέβασμα φωτογραφιών είναι το μόνο πράγμα που συμβαίνει εκεί.`,
      ],
    },
    {
      heading: "Κάντε το ορατό",
      content: [
        `Ακόμη και ένας καλός τρόπος αποδίδει λίγα αν δεν τον προσέξει κανείς, και ελάχιστοι θα ψάξουν από μόνοι τους.`,
        `Τα σημεία που χρησιμοποιούν συνήθως τα ζευγάρια:`,
        {
          type: "list",
          items: [
            `τα τραπέζια`,
            `μια πινακίδα στην είσοδο`,
            `το βιβλίο ευχών`,
            `το μπαρ`,
            `εκεί όπου ήδη βγαίνουν φωτογραφίες`,
            `το προσκλητήριο, όπου χωράει φυσικά`,
          ],
        },
        `Δεν χρειάζεται να είναι παντού. Βάλτε το εκεί όπου ο καλεσμένος έχει ήδη λόγο να κοιτάξει, τη στιγμή που το κινητό είναι πιθανότατα στο χέρι του.`,
      ],
    },
    {
      heading: "Ζητήστε τις μέσα στον γάμο, όχι μόνο μετά",
      content: [
        `Συγκρίνετε δύο αιτήματα.`,
        `Το «στείλτε μας τις φωτογραφίες σας μετά τον γάμο» ζητά από κάποιον να θυμηθεί κάτι, να το ξαναβρεί αργότερα και να κάνει τη δουλειά στον δικό του χρόνο.`,
        `Το «αν βγάλετε μια φωτογραφία που σας άρεσε, ανεβάστε τη εδώ» του ζητά να κάνει κάτι ενώ κρατά το κινητό και βλέπει τη φωτογραφία στην οθόνη.`,
        `Μια σύντομη αναφορά μέσα στη βραδιά κάνει περισσότερα από μια κάρτα μόνη της. Ζητήστε τις όμως και μετά: μια συλλογή που μένει ανοιχτή προλαβαίνει και όσους χόρευαν.`,
        `Και ζητήστε το με τα λόγια σας. Ένα «θα θέλαμε πολύ να δούμε τη βραδιά και από τα δικά σας μάτια» πιάνει περισσότερο από μια οδηγία.`,
      ],
    },
    {
      heading: "Και τα βίντεο;",
      content: [
        `Οι καλεσμένοι τραβούν πια περισσότερο βίντεο από παλιά, και τα κλιπ είναι συχνά αυτά που δεν έχει κανείς άλλος:`,
        {
          type: "list",
          items: [
            `τις ευχές και τα λόγια, ολόκληρα`,
            `την πίστα, από μέσα`,
            `μικρές αυθόρμητες στιγμές ανάμεσα στις προγραμματισμένες`,
            `τα παρασκήνια της προετοιμασίας`,
          ],
        },
        `Το βίντεο είναι εκεί όπου οι τρόποι διαφέρουν περισσότερο: οι υπηρεσίες δέχονται άλλα αρχεία, άλλα μεγέθη και άλλες διάρκειες, και το τι γίνεται με την ποιότητα μετά το ανέβασμα δεν είναι το ίδιο παντού.`,
        `Αν το βίντεο σάς ενδιαφέρει, δοκιμάστε το με ένα πραγματικό κλιπ από κινητό, αντί να υποθέσετε ότι όποιος δέχεται φωτογραφίες δέχεται και βίντεο.`,
      ],
    },
    {
      heading: "Ιδιωτικότητα και πρόσβαση",
      content: [
        `Το υλικό των καλεσμένων είναι προσωπικό, και μέσα σε αυτό υπάρχουν άνθρωποι που δεν διάλεξαν το εργαλείο. Πριν μοιράσετε έναν σύνδεσμο παντού, ξέρετε:`,
        {
          type: "list",
          items: [
            `ποιος μπορεί να δει τη συλλογή`,
            `ποιος μπορεί να προσθέσει σε αυτήν`,
            `αν ένας δημόσιος σύνδεσμος την ανοίγει σε όποιον τον βρει`,
            `για πόσο διάστημα μένει διαθέσιμη`,
            `πώς κατεβάζετε ή εξάγετε τα αρχεία μετά`,
          ],
        },
        `Ένας σύνδεσμος που ανοίγει σε όλους είναι βολικός και δημόσιος. Μια συλλογή δεμένη με τον γάμο σας είναι πιο ιδιωτική και θέλει ένα βήμα παραπάνω για να φτάσει κανείς.`,
        `Διαλέξτε συνειδητά ποιο από τα δύο θέλετε — και πείτε το και στους καλεσμένους, γιατί αλλάζει το τι θα ανεβάσουν.`,
      ],
    },
    {
      heading: "Μία συλλογή είναι καλύτερη από πέντε",
      content: [
        `Οι γάμοι καταλήγουν σε ένα γνωστό μοτίβο: ένα group για τους φίλους του ενός, ένα για τους φίλους της άλλης, ένα οικογενειακό, ένα κοινόχρηστο άλμπουμ και ένας σύνδεσμος από έναν ξάδελφο.`,
        `Το καθένα μαζεύει αληθινές φωτογραφίες. Όλα μαζί όμως σας αφήνουν πέντε σημεία να ελέγξετε και πέντε πράγματα να κατεβάσετε, που είναι πρόβλημα συγκέντρωσης και όχι άλμπουμ γάμου.`,
        `Διαλέξτε έναν κύριο προορισμό και αφήστε τα υπόλοιπα να τροφοδοτούν αυτόν. Αν μια φωτογραφία έρθει αλλιώς, προσθέστε τη εσείς εκεί, αντί να ανοίξετε έκτο σημείο.`,
        `Ζητούμενο είναι να απαντάτε σε μία ερώτηση χωρίς να διστάσετε: πού είναι οι φωτογραφίες του γάμου μας;`,
      ],
    },
    {
      heading: "Ένα απλό σχέδιο",
      content: [
        `Μαζεμένα, όλα χωράνε σε οκτώ βήματα:`,
        {
          type: "steps",
          items: [
            `Διαλέξτε ένα κύριο σημείο όπου θα μαζεύονται οι φωτογραφίες.`,
            `Βεβαιωθείτε ότι φτάνουν εύκολα εκεί από το κινητό τους.`,
            `Δοκιμάστε το ανέβασμα μόνοι σας πριν τον γάμο.`,
            `Βάλτε την οδηγία εκεί όπου θα τη δουν.`,
            `Αναφέρετέ το σύντομα μέσα στη βραδιά.`,
            `Αφήστε τους να ανεβάζουν όσο τραβούν.`,
            `Κρατήστε τη συλλογή ανοιχτή και μετά, για όσα έρθουν αργότερα.`,
            `Κατεβάστε ή αρχειοθετήστε το υλικό, σύμφωνα με τους όρους της υπηρεσίας που διαλέξατε.`,
          ],
        },
        `Το τρίτο βήμα είναι αυτό που παραλείπεται σχεδόν πάντα. Δέκα λεπτά με το δικό σας κινητό είναι που κάνουν όλα τα υπόλοιπα να δουλέψουν.`,
      ],
    },
    {
      heading: "Άλμπουμ, group ή σελίδα ανεβάσματος;",
      content: [
        `Η σύγκριση είναι ένα σύνολο συμβιβασμών και όχι μια κατάταξη.`,
        { type: "subheading", text: "Κοινόχρηστο άλμπουμ" },
        {
          type: "list",
          items: [
            `Κόπος για τον καλεσμένο: μικρός έως μέτριος, ανάλογα με την υπηρεσία.`,
            `Οργάνωση: καλή, αφού όλα καταλήγουν σε ένα άλμπουμ.`,
            `Αρχεία: εξαρτάται από την υπηρεσία, οπότε δείτε τι κάνει με τα βίντεο.`,
            `Ταιριάζει: σε ζευγάρια που ζουν ήδη σε αυτό το οικοσύστημα.`,
          ],
        },
        { type: "subheading", text: "Group στο κινητό" },
        {
          type: "list",
          items: [
            `Κόπος για τον καλεσμένο: ελάχιστος, χωρίς τίποτα καινούριο.`,
            `Οργάνωση: φτωχή έως μέτρια, και χειροτερεύει όσο μεγαλώνει η συζήτηση.`,
            `Αρχεία: ενδέχεται να συμπιεστούν, ανάλογα με την πλατφόρμα.`,
            `Ταιριάζει: σε μικρές παρέες και σε χαλαρό μοίρασμα.`,
          ],
        },
        { type: "subheading", text: "Αίτημα μετά τον γάμο" },
        {
          type: "list",
          items: [
            `Κόπος για τον καλεσμένο: μέτριος, γιατί είναι ξεχωριστή δουλειά.`,
            `Οργάνωση: φτωχή, αφού όλα φτάνουν διαφορετικά.`,
            `Αρχεία: εξαρτάται από το πώς τα στέλνει ο καθένας.`,
            `Ταιριάζει: σε λίγους κοντινούς ανθρώπους.`,
          ],
        },
        { type: "subheading", text: "Σελίδα ανεβάσματος" },
        {
          type: "list",
          items: [
            `Κόπος για τον καλεσμένο: μικρός, και γίνεται εκείνη τη στιγμή.`,
            `Οργάνωση: δυνατή, αφού η σελίδα δεν κάνει τίποτα άλλο.`,
            `Αρχεία: εξαρτάται από την υπηρεσία, οπότε δείτε τα όρια για τα βίντεο.`,
            `Ταιριάζει: σε ζευγάρια που θέλουν μία κεντρική συλλογή.`,
          ],
        },
        `Η σελίδα ανεβάσματος δεν είναι αυτόματα η σωστή απάντηση. Είναι αυτή που πιο αξιόπιστα καταλήγει σε μία οργανωμένη συλλογή.`,
      ],
    },
    {
      heading: "Τι να κοιτάξετε πριν διαλέξετε",
      content: [
        `Ό,τι κι αν συγκρίνετε, μια σύντομη λίστα σάς λέει τα περισσότερα:`,
        {
          type: "list",
          items: [
            `απλή εμπειρία για κάποιον που το κάνει πρώτη φορά`,
            `δουλεύει στα κινητά που έχουν πραγματικά οι καλεσμένοι σας`,
            `ένας ξεκάθαρος προορισμός`,
            `υποστήριξη φωτογραφιών που ταιριάζει στο πώς φωτογραφίζουν`,
            `υποστήριξη βίντεο, αν σας ενδιαφέρει το βίντεο`,
            `ρυθμίσεις ιδιωτικότητας που καταλαβαίνετε`,
            `διάστημα διαθεσιμότητας που σας εξυπηρετεί`,
            `τρόπος να κατεβάσετε ή να εξάγετε τα αρχεία μετά`,
            `μία συλλογή, όχι πέντε σκόρπια κανάλια`,
          ],
        },
        `Ό,τι αφήνει κάποιο από αυτά αναπάντητο, δοκιμάστε το πριν από τον γάμο και όχι μετά.`,
      ],
    },
    {
      heading: "Από τις φωτογραφίες των καλεσμένων στο αρχείο του γάμου σας",
      content: [
        `Το μάζεμα είναι η μισή δουλειά. Η άλλη μισή είναι μια συλλογή με την οποία μπορείτε να καθίσετε: υλικό που βλέπετε, κατεβάζετε, στέλνετε στους δικούς σας που το ζητούν και κρατάτε δίπλα στη δουλειά του φωτογράφου σας.`,
        `Αυτή είναι όλη η αλυσίδα: ο καλεσμένος βγάζει μια φωτογραφία, την ανεβάζει, εσείς τη μαζεύετε, μένει οργανωμένη και γίνεται μέρος του αρχείου σας.`,
        {
          type: "note",
          text: `Το άλμπουμ των καλεσμένων υπάρχει σε όλα τα πακέτα του Agapaé, με διαφορετικά όρια στο καθένα.`,
        },
        `Στο Agapaé οι καλεσμένοι φτάνουν στο άλμπουμ από το QR code ή τον σύνδεσμο του γάμου σας και προσθέτουν τις φωτογραφίες και τα βίντεο που τράβηξαν. Το άλμπουμ ανήκει στην εκδήλωσή σας και δεν είναι δημόσια ροή.`,
        `Το πακέτο ορίζει τα όρια: πόσοι καλεσμένοι ανεβάζουν, πόσα χωράει το άλμπουμ, για πόσο μένει ανοιχτό το ανέβασμα πριν και μετά τον γάμο, και αν οι καλεσμένοι μπορούν να ανεβάσουν και βίντεο εκτός από φωτογραφίες.`,
        {
          type: "p",
          text: [
            `Για το ίδιο το προϊόν, δείτε τις `,
            { text: "φωτογραφίες γάμου", href: "/wedding-photo-sharing" },
            `.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Συχνές ερωτήσεις",

  faq: [
    {
      q: `Ποιος είναι ο πιο εύκολος τρόπος να μαζέψω φωτογραφίες από τους καλεσμένους;`,
      a: `Αυτός που ζητά τα λιγότερα από εκείνους: ένα σημείο που ανοίγει από το κινητό μέσα στη γιορτή, χωρίς να χρειάζεται εξήγηση. Μπορεί να είναι ένα κοινόχρηστο άλμπουμ ή μια σελίδα ανεβάσματος. Πιο σημαντικό από το εργαλείο είναι να υπάρχει ένα και όχι πέντε.`,
    },
    {
      q: `Πώς μοιράζονται φωτογραφίες οι καλεσμένοι χωρίς να τις στέλνουν μία μία;`,
      a: `Δώστε τους ένα σημείο όπου ανεβάζουν πολλές μαζί. Και τα κοινόχρηστα άλμπουμ και οι σελίδες ανεβάσματος επιτρέπουν να διαλέξει κανείς πολλά αρχεία από το κινητό του και να τα στείλει μαζεμένα. Σε μια συζήτηση οι ίδιες φωτογραφίες φτάνουν κομμάτι κομμάτι.`,
    },
    {
      q: `Μπορούν οι καλεσμένοι να ανεβάσουν και βίντεο;`,
      a: `Συχνά ναι, αλλά όχι πάντα με τον ίδιο τρόπο, οπότε ελέγξτε το πριν. Οι υπηρεσίες διαφέρουν στα αρχεία που δέχονται, στο μέγεθος και στη διάρκεια, και σε αυτό που γίνεται με την ποιότητα. Δοκιμάστε το με ένα πραγματικό κλιπ από κινητό.`,
    },
    {
      q: `Κοινόχρηστο άλμπουμ ή group στο κινητό;`,
      a: `Το άλμπουμ κρατά τις φωτογραφίες μαζί και έξω από τη συζήτηση, οπότε βρίσκονται και κατεβαίνουν πιο εύκολα. Το group είναι πιο οικείο και δεν θέλει στήσιμο, αλλά οι εικόνες μπερδεύονται με την κουβέντα και ενδέχεται να συμπιεστούν. Αν οι καλεσμένοι σας χρησιμοποιούν ήδη άλμπουμ, είναι το πιο τακτοποιημένο από τα δύο.`,
    },
    {
      q: `Πότε να τους ζητήσω τις φωτογραφίες;`,
      a: `Μέσα στον γάμο, και ξανά μετά. Η αναφορά εκείνη τη στιγμή τους βρίσκει με τη φωτογραφία στην οθόνη και κοστίζει μία κίνηση. Η συλλογή που μένει ανοιχτή μετά πιάνει όσους χόρευαν και όσους θέλουν πρώτα να δουν με την ησυχία τους το κινητό τους.`,
    },
  ],

  related: {
    heading: "Πού ταιριάζει αυτό στο Agapaé",

    guides: ["wedding-qr-code-for-photos"],

    product: [
      {
        href: "/wedding-photo-sharing",
        label: "Φωτογραφίες γάμου",
        blurb:
          "Δείτε πώς οι καλεσμένοι ανεβάζουν φωτογραφίες και βίντεο σε ένα άλμπουμ που ανήκει στον γάμο σας, φτάνοντας σε αυτό από QR code ή σύνδεσμο. Υπάρχει σε όλα τα πακέτα, με διαφορετικά όρια στο καθένα.",
      },
    ],
  },
};

export default howToCollectWeddingPhotosFromGuestsEl;
